import { useEffect, useState, useRef } from 'react';

import useDebounceCallback from '@lib/core/src/hooks/useDebounceCallback';
import { Stack } from '@mui/material';

import { useAnalytics } from '@/analytics/useAnalytics';
import { MeetingAnalysisDto, usePatchMeetingAnalysis } from '@/api/generated';
import {
  CalendarMeetingDto,
  MeetingTemplateSection,
  MeetingTemplateSectionType
} from '@/api/generated';
import MeetingDataSkeleton from '@/components/Meeting/components/MeetingData/MeetingDataSkeleton';
import {
  SectionContent,
  SectionItem,
  SectionTitleText
} from '@/components/Meeting/components/MeetingOverview/MeetingOverviewStyles';
import MeetingOverviewTasks from '@/components/Meeting/components/MeetingOverview/MeetingOverviewTasks';
import EditableMarkdown from '@/components/common/EditableTextMarkdown';
import Markdown, { convertDoubleNewlinesToSingle } from '@/utils/Markdown';

type Section = MeetingTemplateSection & { content: string };

interface MeetingOverviewSectionsProps {
  meetingAnalysis?: MeetingAnalysisDto | undefined;
  sections: Section[];
  isDefaultTemplate: boolean;
  isLoadingMeetingAnalysis: boolean;
  onRetryClicked?: () => void;
  meeting?: CalendarMeetingDto;
}

const MeetingOverviewSections = ({
  meetingAnalysis,
  sections,
  isDefaultTemplate,
  isLoadingMeetingAnalysis,
  onRetryClicked,
  meeting
}: MeetingOverviewSectionsProps) => {
  const [localSections, setLocalSections] = useState<Section[]>(sections);
  const { mutate: patchMeetingAnalysis } = usePatchMeetingAnalysis();
  const { captureEvent } = useAnalytics();
  const hasUserChanges = useRef(false);

  useEffect(() => {
    setLocalSections(sections);
    hasUserChanges.current = false;
  }, [sections]);

  const editTitle = (index: number, title: string) => {
    hasUserChanges.current = true;
    setLocalSections(prev =>
      prev.map(section => (section.index === index ? { ...section, title } : section))
    );
  };

  const editContent = (index: number, content: string) => {
    hasUserChanges.current = true;
    setLocalSections(prev =>
      prev.map(section => (section.index === index ? { ...section, content } : section))
    );
  };

  const handleSave = (sections: Section[]) => {
    if (sections.length === 0) return;
    if (!meeting?.id || !meetingAnalysis) return;
    if (!hasUserChanges.current) return;

    patchMeetingAnalysis(
      {
        id: meetingAnalysis.id,
        data: {
          ...meetingAnalysis,
          sections
        }
      },
      {
        onSuccess: () => {
          captureEvent('meeting_summary_edited', {
            meetingId: meeting?.id,
            meetingAnalysisId: meetingAnalysis.id
          });
        }
      }
    );
  };

  useDebounceCallback(localSections, 400, handleSave);

  if (isLoadingMeetingAnalysis) return <MeetingDataSkeleton />;

  // TODO: Replce this once we solve backend issue
  if (localSections?.length && !isDefaultTemplate)
    return (
      <Stack gap="24px" sx={{ marginTop: '10px' }}>
        {localSections.map((section, idx) => (
          <SectionItem key={section.index}>
            <Stack key={section.index} gap={'12px'}>
              {/* <SectionTitle
                value={section.title}
                onChange={e => editTitle(section.index!, e.target.value)}
                sx={idx === 0 ? { width: 'calc(100% - 200px)' } : undefined}
              /> */}
              <SectionTitleText>{section.title}</SectionTitleText>
              {section?.type === MeetingTemplateSectionType.tasks ? (
                <MeetingOverviewTasks meeting={meeting} />
              ) : (
                <SectionContent>
                  {meetingAnalysis ? (
                    <EditableMarkdown
                      value={convertDoubleNewlinesToSingle(section.content || '')}
                      callback={content => editContent(section.index!, content)}
                    />
                  ) : (
                    <Markdown>{convertDoubleNewlinesToSingle(section.content || '')}</Markdown>
                  )}
                </SectionContent>
              )}
            </Stack>
          </SectionItem>
        ))}
      </Stack>
    );

  return null;
};

export default MeetingOverviewSections;
